/* ============================================================================
   UI / filter — search, filtering, choosing among options.
   SearchField, FilterMenu, FilterChips, MultiSelect, Tabs, Segmented (pill choices), ToggleChips (pill choices, any number on).
   ============================================================================ */
import React, { useId } from "react";
import { Check, ChevronDown, ListFilter, Search, X } from "lucide-react";
import { Button } from "./actions.jsx";
import { Checkbox } from "./form.jsx";
import { useMenu } from "./menu.jsx";
import { Tooltip } from "./overlay.jsx";
import { cx } from "./util.js";

/* ── Search and filter ────────────────────────────────────────────────────────
   One search field and one filter control for every screen. A screen passes its
   state in and gets the same look, keyboard behaviour and focus handling. */
export const SearchField = React.forwardRef(function SearchField({ value, onChange, placeholder = "Search", className, ...props }, ref) {
  return (
    <label className={cx("ds-search", className)}>
      <Search size={14} aria-hidden="true" />
      <input ref={ref} type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder} {...props} />
      {value ? (
        <button type="button" className="ds-search__clear" aria-label="Clear search" onClick={() => onChange("")}>
          <X size={14} aria-hidden="true" />
        </button>
      ) : null}
    </label>
  );
});

/**
 * groups: [{ key, label, options: [{ value, label }], multiple?, defaultValue? }]
 * value:  { [group.key]: selected value, or an array of values when the group is multiple }
 * onChange(groupKey, nextValue)
 * The trigger shows how many groups are away from their default; FilterChips shows which.
 */
const isDefault = (group, current) => (group.multiple
  ? !current || current.length === 0
  : (current ?? group.defaultValue) === group.defaultValue);

/* One button that opens the filters of a list (groups of options, single or multiple) and shows how many are on. */
export function FilterMenu({ groups, value, onChange, label = "Filter" }) {
  const { open, setOpen, alignRight, root, trigger, menu } = useMenu();
  const menuId = useId();
  const active = groups.filter((g) => !isDefault(g, value[g.key])).length;

  const choose = (group, option) => {
    if (group.multiple) {
      const current = value[group.key] || [];
      onChange(group.key, current.includes(option.value) ? current.filter((v) => v !== option.value) : [...current, option.value]);
    } else {
      onChange(group.key, option.value);
      setOpen(false);
      trigger.current?.focus();
    }
  };
  const selected = (group, option) => (group.multiple
    ? (value[group.key] || []).includes(option.value)
    : (value[group.key] ?? group.defaultValue) === option.value);

  return (
    <span className="ds-menu-anchor" ref={root}>
      <button type="button" ref={trigger} className="ds-btn" aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined} onClick={() => setOpen((v) => !v)}>
        <ListFilter size={14} aria-hidden="true" />{label}
        {active ? <span className="ds-count">{active}</span> : null}
      </button>
      {open ? (
        <div id={menuId} ref={menu} role="menu" className={cx("ds-menu", alignRight && "ds-menu--right")}>
          {groups.map((group) => (
            <div key={group.key} role="group" aria-label={group.label}>
              <div className="ds-menu__label">{group.label}</div>
              {group.options.map((option) => (
                <button key={option.value} type="button" role={group.multiple ? "menuitemcheckbox" : "menuitemradio"} aria-checked={selected(group, option)} className="ds-menu__item" onClick={() => choose(group, option)}>
                  <span>{option.label}</span>
                  {selected(group, option) ? <Check size={14} aria-hidden="true" /> : null}
                </button>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </span>
  );
}

/** The applied filters, one chip each; the x puts that group back to its default. */
export function FilterChips({ groups, value, onChange }) {
  const chips = [];
  groups.forEach((group) => {
    if (isDefault(group, value[group.key])) return;
    const chosen = group.multiple ? value[group.key] : [value[group.key]];
    chosen.forEach((v) => {
      const option = group.options.find((o) => o.value === v);
      chips.push({ key: `${group.key}:${v}`, group, v, text: `${group.label}: ${option ? option.label : v}` });
    });
  });
  if (!chips.length) return null;
  return (
    <div className="ds-toolbar" role="list" aria-label="Applied filters">
      {chips.map(({ key, group, v, text }) => (
        <span key={key} role="listitem" className="ds-chip">
          {text}
          <button type="button" aria-label={`Remove ${text}`} onClick={() => onChange(group.key, group.multiple ? (value[group.key] || []).filter((x) => x !== v) : group.defaultValue)}>
            <X size={12} aria-hidden="true" />
          </button>
        </span>
      ))}
    </div>
  );
}

/* A dropdown of tick boxes for "show these": the trigger says what is chosen ("Stage (3)"), the panel has Select all
   and Clear. options: [{ value, label }] (a label can be a dot and a name); selected is a Set. */
export function MultiSelect({ label, options, selected, onToggle, onSelectAll, onClear, className }) {
  const { open, setOpen, alignRight, root, trigger, menu } = useMenu();
  const menuId = useId();
  const chosen = selected.size;
  const summary = chosen === 0 ? `${label} (none)`
    : chosen === options.length ? label
      : chosen === 1 ? options.find((o) => selected.has(o.value))?.label || label
        : `${label} (${chosen})`;
  return (
    <span className={cx("ds-menu-anchor", className)} ref={root}>
      <button type="button" ref={trigger} className="ds-btn ds-btn--field" aria-haspopup="true" aria-expanded={open} aria-controls={open ? menuId : undefined} onClick={() => setOpen((v) => !v)}>
        {summary}<ChevronDown size={14} aria-hidden="true" />
      </button>
      {open ? (
        <div id={menuId} ref={menu} className={cx("ds-menu ds-menu--scroll", alignRight && "ds-menu--right")}>
          <div className="ds-menu__actions">
            <Button type="button" variant="link" size="sm" onClick={onSelectAll}>Select all</Button>
            <Button type="button" variant="link" size="sm" onClick={onClear}>Clear</Button>
          </div>
          {options.map((o) => (
            <Checkbox key={o.value} className="ds-menu__check" checked={selected.has(o.value)} onChange={() => onToggle(o.value)}
              label={o.label} />
          ))}
        </div>
      ) : null}
    </span>
  );
}

/* Tabs across a page: tabs [{ key, label }], the open one in `value`, onChange(key). Use for peer views of one subject. */
export function Tabs({ tabs, value, onChange, label, id }) {
  // With an `id` the tabs follow the full tablist pattern: each tab names its panel (`${id}-tabpanel`), only the
  // selected tab is in the Tab order, and the arrow keys move between tabs.
  const onKeyDown = id ? (event) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    const index = tabs.findIndex((t) => t.key === value);
    const next = tabs[(index + (event.key === "ArrowRight" ? 1 : tabs.length - 1)) % tabs.length];
    onChange(next.key);
    document.getElementById(`${id}-tab-${next.key}`)?.focus();
  } : undefined;
  return (
    <div className="ds-tabs" role="tablist" aria-label={label} onKeyDown={onKeyDown}>
      {tabs.map((tab) => (
        <button key={tab.key} type="button" role="tab" aria-selected={value === tab.key} onClick={() => onChange(tab.key)}
          id={id ? `${id}-tab-${tab.key}` : undefined} aria-controls={id ? `${id}-tabpanel` : undefined}
          tabIndex={id && value !== tab.key ? -1 : undefined}>{tab.label}</button>
      ))}
    </div>
  );
}

/* A row of pill choices of which one is on (a deal size, a period). options: [{ value, label, tip, disabled }]; `tip` shows
   on hover as a Tooltip; `disabled` greys one out. Each is a button with aria-pressed. */
export function Segmented({ options, value, onChange, label, className }) {
  return (
    <div role="group" aria-label={label} className={cx("ds-segmented", className)}>
      {options.map((o) => {
        const button = <button key={o.value} type="button" aria-pressed={value === o.value} disabled={o.disabled} onClick={() => onChange(o.value)}>{o.label}</button>;
        return o.tip ? <Tooltip key={o.value} content={o.tip} width={o.tipWidth} block>{button}</Tooltip> : button;
      })}
    </div>
  );
}

/* A row of chips of which any number are on (the days a person works): each is a button with aria-pressed, and
   onToggle(value) flips one. options: [{ value, label, title }]; `value` lists the ones that are on. It takes Button's
   variant and size, and `chipClassName` for each chip. */
export function ToggleChips({ options, value, onToggle, label, variant, size = "sm", className, chipClassName }) {
  return (
    <div role="group" aria-label={label} className={cx("ds-toggle-chips", className)}>
      {options.map((o) => (
        <Button key={o.value} type="button" variant={variant} size={size} className={chipClassName} title={o.title} aria-pressed={value.includes(o.value)} onClick={() => onToggle(o.value)}>{o.label}</Button>
      ))}
    </div>
  );
}
